import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  GitCompare, 
  Plus, 
  X, 
  TrendingUp, 
  DollarSign, 
  Target, 
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BarChart3,
  Calendar,
  History,
  Filter,
  RefreshCw,
  Sliders,
  ToggleLeft,
  ToggleRight,
  CheckSquare,
  Square,
  Save,
  Layers,
  Scale,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getScenarios, recordProjectionAccuracy } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { ProjectionAccuracyTab } from '../components/compare/ProjectionAccuracyTab';
import { TrendAnalysisTab } from '../components/compare/TrendAnalysisTab';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const ANALYSIS_TYPES = {
  single: 'Single Measure',
  portfolio: 'Portfolio',
  matrix: 'Full Matrix'
};

// What-If Mode metric definitions
const WHATIF_METRICS = [
  { id: 'roi', label: 'ROI', icon: TrendingUp, color: 'text-teal-600' },
  { id: 'financial_impact', label: 'Financial Impact', icon: DollarSign, color: 'text-emerald-600' },
  { id: 'net_benefit', label: 'Net Benefit', icon: DollarSign, color: 'text-teal-600' },
  { id: 'kpi_uplift', label: 'KPI Uplift', icon: BarChart3, color: 'text-purple-600' },
  { id: 'payback', label: 'Payback Period', icon: Clock, color: 'text-orange-600' },
];

const CompareScenarios = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currency } = useApp();
  const [scenarios, setScenarios] = useState([]);
  const [scenarioGroups, setScenarioGroups] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([null, null]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('compare'); // 'compare' or 'trends'
  const [filterType, setFilterType] = useState('all'); // 'all', 'single', 'portfolio', 'matrix'
  
  // What-If Mode state
  const [whatIfEnabled, setWhatIfEnabled] = useState(false);
  const [whatIfSliders, setWhatIfSliders] = useState({}); // { scenarioId: leiValue }
  const [selectedMetrics, setSelectedMetrics] = useState(['roi', 'financial_impact', 'net_benefit', 'kpi_uplift', 'payback']); // All selected by default
  
  // Save Projected Scenario state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveMode, setSaveMode] = useState('single'); // 'single' or 'batch'
  const [scenarioToSave, setScenarioToSave] = useState(null);
  const [projectedName, setProjectedName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Compute projected scenario pairs for Projection vs Actual tab
  const projectedPairs = useMemo(() => {
    const pairs = [];
    const projectedScenarios = scenarios.filter(s => s.is_projected && s.original_scenario_id);
    
    for (const projected of projectedScenarios) {
      const original = scenarios.find(s => s.id === projected.original_scenario_id);
      if (original) {
        pairs.push({ projected, original });
      }
    }
    return pairs;
  }, [scenarios]);

  useEffect(() => {
    loadData();
  }, []);

  // Preselect scenarios passed via ?ids=a,b,c,d (from Saved Scenarios "Compare selected").
  useEffect(() => {
    const ids = (searchParams.get('ids') || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (ids.length && scenarios.length) {
      const picked = ids.map((id) => scenarios.find((s) => s.id === id)).filter(Boolean).slice(0, 4);
      if (picked.length) {
        const slots = [...picked];
        while (slots.length < 2) slots.push(null);
        setSelectedScenarios(slots);
        const types = new Set(picked.map((s) => s.analysis_type || 'single'));
        setFilterType(types.size === 1 ? [...types][0] : 'all');
      }
    }
  }, [scenarios, searchParams]);

  const loadData = async () => {
    try {
      const [scenariosRes, groupsRes] = await Promise.all([
        getScenarios(),
        api.get(`/scenario-groups`).catch(() => ({ data: [] }))
      ]);
      setScenarios(scenariosRes.data);
      setScenarioGroups(groupsRes.data);
    } catch (error) {
      logger.error('Error loading data:', error);
      toast.error('Failed to load scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter scenarios by analysis type
  const filteredScenarios = filterType === 'all' 
    ? scenarios 
    : scenarios.filter(s => (s.analysis_type || 'single') === filterType);

  // Get unique analysis types from scenarios
  const availableTypes = [...new Set(scenarios.map(s => s.analysis_type || 'single'))];

  const handleSelectScenario = (index, scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    const newSelected = [...selectedScenarios];
    newSelected[index] = scenario;
    
    // Auto-filter to matching type when first scenario is selected
    if (index === 0 && scenario) {
      setFilterType(scenario.analysis_type || 'single');
    }
    
    setSelectedScenarios(newSelected);
  };

  const handleSelectGroup = (groupId) => {
    const group = scenarioGroups.find(g => g.group_id === groupId);
    setSelectedGroup(group);
  };

  const addComparisonSlot = () => {
    if (selectedScenarios.length < 4) {
      setSelectedScenarios([...selectedScenarios, null]);
    }
  };

  const removeComparisonSlot = (index) => {
    if (selectedScenarios.length > 2) {
      const newSelected = selectedScenarios.filter((_, i) => i !== index);
      setSelectedScenarios(newSelected);
    }
  };

  const getMetricValue = (scenario, metric) => {
    if (!scenario?.results) return 0;
    const r = scenario.results;
    switch (metric) {
      case 'roi': return r.roi_percent || r.total_roi_percent || 0;
      case 'financial_impact': return r.financial_impact || r.total_financial_impact || 0;
      case 'net_benefit': return r.net_benefit || r.total_net_benefit || 0;
      case 'kpi_uplift': return r.kpi_delta_percent || r.total_kpi_delta_percent || 0;
      case 'payback': return r.payback_months || 0;
      case 'lei_improvement': return scenario.configuration?.lei_improvement || 0;
      case 'intervention_cost': return scenario.configuration?.intervention_cost || 0;
      default: return 0;
    }
  };

  // What-If simulation: Calculate metrics based on simulated LEI improvement
  const getSimulatedMetricValue = useCallback((scenario, metric, simulatedLei) => {
    if (!scenario?.results) return 0;
    
    const originalLei = scenario.configuration?.lei_improvement || 1;
    if (originalLei === 0) return getMetricValue(scenario, metric);
    
    const ratio = simulatedLei / originalLei;
    const originalValue = getMetricValue(scenario, metric);
    
    const diminishingRatio = ratio > 1 ? 1 + Math.sqrt(ratio - 1) * 0.7 : ratio;
    
    switch (metric) {
      case 'roi':
      case 'financial_impact':
      case 'net_benefit':
      case 'kpi_uplift':
        return originalValue * diminishingRatio;
      case 'payback':
        return ratio > 0 ? originalValue / diminishingRatio : originalValue;
      default:
        return originalValue;
    }
  }, []);

  // Get the effective metric value (simulated if What-If mode and metric selected, otherwise original)
  const getEffectiveMetricValue = useCallback((scenario, metric) => {
    if (!whatIfEnabled || !selectedMetrics.includes(metric)) {
      return getMetricValue(scenario, metric);
    }
    const simulatedLei = whatIfSliders[scenario?.id] ?? getMetricValue(scenario, 'lei_improvement');
    return getSimulatedMetricValue(scenario, metric, simulatedLei);
  }, [whatIfEnabled, selectedMetrics, whatIfSliders, getSimulatedMetricValue]);

  // Initialize What-If sliders when scenarios change
  useEffect(() => {
    if (whatIfEnabled) {
      const newSliders = {};
      selectedScenarios.filter(s => s !== null).forEach(s => {
        if (!(s.id in whatIfSliders)) {
          newSliders[s.id] = s.configuration?.lei_improvement || 10;
        }
      });
      if (Object.keys(newSliders).length > 0) {
        setWhatIfSliders(prev => ({ ...prev, ...newSliders }));
      }
    }
  }, [selectedScenarios, whatIfEnabled, whatIfSliders]);

  // Toggle all metrics
  const toggleAllMetrics = () => {
    if (selectedMetrics.length === WHATIF_METRICS.length) {
      setSelectedMetrics([]);
    } else {
      setSelectedMetrics(WHATIF_METRICS.map(m => m.id));
    }
  };

  // Toggle individual metric
  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => 
      prev.includes(metricId) 
        ? prev.filter(m => m !== metricId)
        : [...prev, metricId]
    );
  };

  // Open save dialog for single scenario
  const openSaveSingleDialog = (scenario) => {
    const simulatedLei = whatIfSliders[scenario.id] ?? (scenario.configuration?.lei_improvement || 0);
    setScenarioToSave(scenario);
    setSaveMode('single');
    setProjectedName(`${scenario.name} - Projected ${simulatedLei.toFixed(0)}%`);
    setSaveDialogOpen(true);
  };

  // Open save dialog for batch (all scenarios)
  const openSaveBatchDialog = () => {
    setSaveMode('batch');
    setProjectedName(`Projected Comparison Set - ${new Date().toLocaleDateString()}`);
    setSaveDialogOpen(true);
  };

  // Build projected results for a scenario
  const buildProjectedResults = (scenario) => {
    return {
      roi_percent: getEffectiveMetricValue(scenario, 'roi'),
      financial_impact: getEffectiveMetricValue(scenario, 'financial_impact'),
      net_benefit: getEffectiveMetricValue(scenario, 'net_benefit'),
      kpi_delta_percent: getEffectiveMetricValue(scenario, 'kpi_uplift'),
      payback_months: getEffectiveMetricValue(scenario, 'payback'),
      // Keep original Monte Carlo data if available
      roi_mean: scenario.results?.roi_mean,
      roi_p5: scenario.results?.roi_p5,
      roi_p95: scenario.results?.roi_p95,
    };
  };

  // Save projected scenario(s)
  const saveProjectedScenario = async () => {
    setIsSaving(true);
    try {
      if (saveMode === 'single' && scenarioToSave) {
        const simulatedLei = whatIfSliders[scenarioToSave.id] ?? (scenarioToSave.configuration?.lei_improvement || 0);
        const projectedData = {
          name: projectedName,
          analysis_type: scenarioToSave.analysis_type || 'single',
          is_projected: true,
          original_scenario_id: scenarioToSave.id,
          configuration: {
            ...scenarioToSave.configuration,
            lei_improvement: simulatedLei,
            original_lei_improvement: scenarioToSave.configuration?.lei_improvement,
          },
          results: buildProjectedResults(scenarioToSave),
        };
        
        await api.post(`/scenarios`, projectedData);
        toast.success('Projected scenario saved successfully!');
      } else if (saveMode === 'batch') {
        // Save all adjusted scenarios as a batch
        const scenariosToSave = selectedScenarios.filter(s => s !== null);
        const savedIds = [];
        
        for (const scenario of scenariosToSave) {
          const simulatedLei = whatIfSliders[scenario.id] ?? (scenario.configuration?.lei_improvement || 0);
          const projectedData = {
            name: `${projectedName} - ${scenario.name.substring(0, 20)}`,
            analysis_type: scenario.analysis_type || 'single',
            is_projected: true,
            original_scenario_id: scenario.id,
            projected_set_name: projectedName,
            configuration: {
              ...scenario.configuration,
              lei_improvement: simulatedLei,
              original_lei_improvement: scenario.configuration?.lei_improvement,
            },
            results: buildProjectedResults(scenario),
          };
          
          const res = await api.post(`/scenarios`, projectedData);
          savedIds.push(res.data.id);
        }
        
        toast.success(`${savedIds.length} projected scenarios saved as a set!`);
      }
      
      setSaveDialogOpen(false);
      loadData(); // Refresh scenarios list
    } catch (error) {
      logger.error('Error saving projected scenario:', error);
      toast.error('Failed to save projected scenario');
    } finally {
      setIsSaving(false);
    }
  };

  const getDifference = (val1, val2) => {
    if (!val1 || !val2) return null;
    return ((val2 - val1) / Math.abs(val1)) * 100;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const DifferenceIndicator = ({ diff, inverse = false }) => {
    if (diff === null || diff === undefined || isNaN(diff)) return <Minus className="h-4 w-4 text-slate-400" />;
    const isPositive = inverse ? diff < 0 : diff > 0;
    const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
    const color = isPositive ? 'text-emerald-600' : 'text-rose-600';
    return (
      <span className={`flex items-center gap-1 text-xs font-medium ${color}`}>
        <Icon className="h-3 w-3" />
        {Math.abs(diff).toFixed(1)}%
      </span>
    );
  };

  // Prepare chart data for comparison - with What-If support
  const chartData = useMemo(() => {
    return selectedScenarios
      .filter(s => s !== null)
      .map(s => {
        const data = {
          name: s.name.length > 30 ? s.name.substring(0, 30) + '...' : s.name,
          fullName: s.name,
        };
        
        if (whatIfEnabled) {
          // Original values (gray bars)
          data['ROI (Original)'] = getMetricValue(s, 'roi') / 100;
          data['KPI Uplift (Original)'] = getMetricValue(s, 'kpi_uplift');
          // Simulated values (colored bars)
          data['ROI (Simulated)'] = selectedMetrics.includes('roi') 
            ? getEffectiveMetricValue(s, 'roi') / 100 
            : getMetricValue(s, 'roi') / 100;
          data['KPI Uplift (Simulated)'] = selectedMetrics.includes('kpi_uplift')
            ? getEffectiveMetricValue(s, 'kpi_uplift')
            : getMetricValue(s, 'kpi_uplift');
        } else {
          data['ROI'] = getMetricValue(s, 'roi') / 100;
          data['KPI Uplift'] = getMetricValue(s, 'kpi_uplift');
        }
        
        return data;
      });
  }, [selectedScenarios, whatIfEnabled, selectedMetrics, getEffectiveMetricValue]);

  // Prepare radar chart data
  const radarData = [
    { metric: 'ROI', fullMark: 10 },
    { metric: 'KPI Uplift', fullMark: 30 },
    { metric: 'Net Benefit', fullMark: 100 },
    { metric: 'Payback Speed', fullMark: 10 },
    { metric: 'Cost Efficiency', fullMark: 100 },
  ].map(item => {
    const dataPoint = { metric: item.metric, fullMark: item.fullMark };
    selectedScenarios.filter(s => s !== null).forEach((s, idx) => {
      let value = 0;
      switch (item.metric) {
        case 'ROI': value = Math.min(getEffectiveMetricValue(s, 'roi') / 100, 10); break;
        case 'KPI Uplift': value = Math.min(getEffectiveMetricValue(s, 'kpi_uplift'), 30); break;
        case 'Net Benefit': value = Math.min(getEffectiveMetricValue(s, 'net_benefit') / 10000, 100); break;
        case 'Payback Speed': value = Math.max(0, 10 - (getEffectiveMetricValue(s, 'payback') / 2)); break;
        case 'Cost Efficiency': value = Math.min((getEffectiveMetricValue(s, 'net_benefit') / Math.max(getMetricValue(s, 'intervention_cost'), 1)) * 100, 100); break;
        default: value = 0;
      }
      dataPoint[`scenario${idx + 1}`] = value;
    });
    return dataPoint;
  });

  const colors = ['#0d9488', '#1e3a5f', '#9333ea', '#ea580c'];
  const selectedCount = selectedScenarios.filter(s => s !== null).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Compare Scenarios"
        description="Side-by-side comparison of saved scenarios."
        theme={__pageTheme}
        icon={GitCompare}
        testId="compare-scenarios-hero"
        actions={(
          /* Dark-mode segmented control mirroring the
             AnalysisModeToggle look so the three view modes
             live in the hero header instead of below it. */
          <div
            className="inline-flex items-center rounded-lg bg-white border border-slate-200 p-1 gap-0.5"
            role="tablist"
            data-testid="compare-view-mode-toggle"
          >
            {[
              { key: 'compare', label: 'Compare Scenarios', Icon: GitCompare },
              { key: 'accuracy', label: 'Projection vs Actual', Icon: Scale },
              { key: 'trends', label: 'Trend Analysis', Icon: History },
            ].map(({ key, label, Icon }) => {
              const active = viewMode === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setViewMode(key)}
                  data-testid={`compare-view-${key}`}
                  className={
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ' +
                    (active
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-700 hover:text-slate-700 hover:bg-slate-50')
                  }
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                  {key === 'accuracy' && projectedPairs.length > 0 && (
                    <Badge
                      variant="secondary"
                      className={
                        'ml-1 text-[10px] h-4 px-1 ' +
                        (active ? '' : 'bg-white border border-slate-200 text-slate-900 border-slate-200')
                      }
                    >
                      {projectedPairs.length}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        )}
      />
    )}>
    <div className="-mt-2 md:-mt-4 space-y-4 animate-fade-in">
      {/* Header - matching standard style */}
      
      {/* View Mode Tabs (TabsList moved into the hero header
          above; we keep the Tabs wrapper so TabsContent still
          works). */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">

        {/* ==================== COMPARE TAB ==================== */}
        <TabsContent value="compare" className="mt-6 space-y-6">
          {/* Filter Bar */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filter by type:</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterType === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('all')}
                className={filterType === 'all' ? 'bg-navy-700' : ''}
              >
                All ({scenarios.length})
              </Button>
              {availableTypes.map(type => {
                const count = scenarios.filter(s => (s.analysis_type || 'single') === type).length;
                const canCompare = count >= 2;
                return (
                  <Button
                    key={type}
                    variant={filterType === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterType(type)}
                    className={`${filterType === type ? 'bg-navy-700' : ''} ${!canCompare ? 'opacity-60' : ''}`}
                    title={canCompare ? `${count} scenarios available` : `Only ${count} scenario - need 2+ to compare`}
                  >
                    {ANALYSIS_TYPES[type] || type} ({count})
                    {!canCompare && <span className="ml-1 text-amber-500">*</span>}
                  </Button>
                );
              })}
            </div>
            <div className="ml-auto text-xs text-slate-500">
              {filteredScenarios.length} scenarios
            </div>
          </div>

          {/* What-If Mode Controls */}
          <Card className={`transition-all duration-300 ${whatIfEnabled ? 'border-purple-300 bg-purple-50/30' : 'border-slate-200'}`}>
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${whatIfEnabled ? 'bg-purple-100' : 'bg-slate-100'}`}>
                    <Sliders className={`h-5 w-5 ${whatIfEnabled ? 'text-purple-600' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      What-If Mode
                      <Badge variant={whatIfEnabled ? 'default' : 'secondary'} className={whatIfEnabled ? 'bg-purple-600' : ''}>
                        {whatIfEnabled ? 'Active' : 'Off'}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Simulate different LEI improvement values to see projected impact
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="whatif-toggle" className="text-sm text-slate-600">
                    {whatIfEnabled ? 'Enabled' : 'Disabled'}
                  </Label>
                  <Switch
                    id="whatif-toggle"
                    checked={whatIfEnabled}
                    onCheckedChange={setWhatIfEnabled}
                    data-testid="whatif-mode-toggle"
                  />
                </div>
              </div>
            </CardHeader>
            
            {whatIfEnabled && selectedCount >= 2 && (
              <CardContent className="pt-0 space-y-4">
                {/* Metric Selector */}
                <div className="p-3 bg-white rounded-lg border border-purple-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-700">Metrics to Simulate:</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleAllMetrics}
                      className="text-xs h-7 px-2"
                      data-testid="toggle-all-metrics"
                    >
                      {selectedMetrics.length === WHATIF_METRICS.length ? (
                        <><CheckSquare className="h-3.5 w-3.5 mr-1" /> Deselect All</>
                      ) : (
                        <><Square className="h-3.5 w-3.5 mr-1" /> Select All</>
                      )}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {WHATIF_METRICS.map(metric => {
                      const Icon = metric.icon;
                      const isSelected = selectedMetrics.includes(metric.id);
                      return (
                        <button
                          key={metric.id}
                          onClick={() => toggleMetric(metric.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            isSelected 
                              ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                              : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                          }`}
                          data-testid={`metric-toggle-${metric.id}`}
                        >
                          <Icon className="h-3 w-3" />
                          {metric.label}
                          {isSelected && <X className="h-3 w-3 ml-1" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* LEI Sliders for each scenario */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedScenarios.filter(s => s !== null).map((scenario, idx) => {
                    const originalLei = scenario.configuration?.lei_improvement || 10;
                    const currentLei = whatIfSliders[scenario.id] ?? originalLei;
                    const changePercent = originalLei > 0 ? ((currentLei - originalLei) / originalLei * 100) : 0;
                    
                    return (
                      <div 
                        key={scenario.id}
                        className="p-4 bg-white rounded-lg border border-purple-100"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <div 
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                            style={{ backgroundColor: colors[idx] }}
                          >
                            {idx + 1}
                          </div>
                          <span className="font-medium text-sm text-slate-800 truncate flex-1">
                            {scenario.name.length > 25 ? scenario.name.substring(0, 25) + '...' : scenario.name}
                          </span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">LEI Improvement %</span>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-purple-700">{currentLei.toFixed(0)}%</span>
                              {changePercent !== 0 && (
                                <span className={`text-xs font-medium ${changePercent > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  ({changePercent > 0 ? '+' : ''}{changePercent.toFixed(0)}%)
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <Slider
                            value={[currentLei]}
                            onValueChange={([value]) => setWhatIfSliders(prev => ({ ...prev, [scenario.id]: value }))}
                            min={0}
                            max={100}
                            step={1}
                            className="w-full"
                            data-testid={`whatif-slider-${idx}`}
                          />
                          
                          <div className="flex justify-between text-[10px] text-slate-400">
                            <span>0%</span>
                            <span className="text-purple-500">Original: {originalLei}%</span>
                            <span>100%</span>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex-1 text-xs h-7 text-slate-500 hover:text-purple-600"
                              onClick={() => setWhatIfSliders(prev => ({ ...prev, [scenario.id]: originalLei }))}
                            >
                              Reset to Original
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-xs h-7 border-purple-300 text-purple-600 hover:bg-purple-50"
                              onClick={() => openSaveSingleDialog(scenario)}
                              data-testid={`save-projected-${idx}`}
                            >
                              <Save className="h-3 w-3 mr-1" />
                              Save
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Save All as Projected Set Button */}
                <div className="mt-4 pt-4 border-t border-purple-100">
                  <Button
                    variant="default"
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={openSaveBatchDialog}
                    data-testid="save-all-projected"
                  >
                    <Layers className="h-4 w-4 mr-2" />
                    Save All as Projected Set
                  </Button>
                  <p className="text-[10px] text-slate-500 text-center mt-2">
                    Saves all {selectedScenarios.filter(s => s !== null).length} scenarios with their simulated values as a comparison set
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Not enough scenarios warning */}
          {filteredScenarios.length < 2 && filterType !== 'all' && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-3">
                <div className="flex items-center gap-2 text-amber-800 text-sm">
                  <Filter className="h-4 w-4" />
                  <span>
                    Only {filteredScenarios.length} {ANALYSIS_TYPES[filterType]} scenario{filteredScenarios.length !== 1 ? 's' : ''} available. 
                    You need at least 2 scenarios of the same type to compare. 
                    <Button 
                      variant="link" 
                      className="text-amber-700 underline p-0 h-auto ml-1"
                      onClick={() => setFilterType('all')}
                    >
                      Show all types
                    </Button>
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scenario Selectors */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Select 2-4 scenarios to compare (same type recommended)</p>
            {selectedScenarios.length < 4 && (
              <Button
                variant="outline"
                size="sm"
                onClick={addComparisonSlot}
                data-testid="add-comparison-slot"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Slot
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {selectedScenarios.map((selected, index) => (
              <Card key={selected?.id || `slot-${index}`} className={`relative ${selected ? 'border-teal-200 bg-teal-50/30' : 'border-dashed'}`}>
                {/* Show clear button on any slot that has a selection, or remove button for slots 3+ */}
                {(selected || index > 1) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6 hover:bg-rose-50 hover:text-rose-600"
                    onClick={() => {
                      if (index > 1) {
                        removeComparisonSlot(index);
                      } else {
                        // Clear selection for slots 1 and 2
                        const newSelected = [...selectedScenarios];
                        newSelected[index] = null;
                        setSelectedScenarios(newSelected);
                        // Reset filter if clearing slot 1
                        if (index === 0) {
                          setFilterType('all');
                        }
                      }
                    }}
                    title={index > 1 ? "Remove slot" : "Clear selection"}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: colors[index] }}
                    >
                      {index + 1}
                    </div>
                    <Select
                      value={selected?.id || ''}
                      onValueChange={(value) => handleSelectScenario(index, value)}
                    >
                      <SelectTrigger data-testid={`scenario-select-${index}`}>
                        <SelectValue placeholder="Select scenario..." />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredScenarios.map((s) => (
                          <SelectItem 
                            key={s.id} 
                            value={s.id}
                            disabled={selectedScenarios.some(sel => sel?.id === s.id)}
                          >
                            <div className="flex items-center gap-2">
                              <span>{s.name}</span>
                              <Badge variant="outline" className="text-[10px]">
                                {ANALYSIS_TYPES[s.analysis_type] || 'Single'}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selected && (
                      <div className="text-xs text-slate-500 space-y-1">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(selected.created_at)}
                        </div>
                        <Badge variant="secondary" className="text-[10px]">
                          {ANALYSIS_TYPES[selected.analysis_type] || 'Single Measure'}
                        </Badge>
                        {selected.version > 1 && (
                          <Badge variant="outline" className="text-[10px] ml-1">
                            v{selected.version}
                          </Badge>
                        )}
                        {selected.constraint_context?.label && (
                          <Badge className="text-[10px] ml-1 bg-orange-100 text-orange-700 hover:bg-orange-100" data-testid={`scenario-constraint-badge-${index}`}>
                            Recovers {selected.constraint_context.label} leak
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Type Mismatch Warning */}
          {selectedCount >= 2 && (
            (() => {
              const types = selectedScenarios.filter(s => s).map(s => s.analysis_type || 'single');
              const allSameType = types.every(t => t === types[0]);
              if (!allSameType) {
                return (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="py-3">
                      <div className="flex items-center gap-2 text-amber-800 text-sm">
                        <Filter className="h-4 w-4" />
                        <span>
                          <strong>Note:</strong> You're comparing different analysis types. 
                          For accurate comparisons, select scenarios of the same type.
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              return null;
            })()
          )}

          {/* Comparison Results */}
          {selectedCount >= 2 && (
            <>
              {/* Key Metrics Comparison */}
              <Card className={whatIfEnabled ? 'border-purple-200' : ''}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-teal-600" />
                    Key Metrics Comparison
                    {whatIfEnabled && <Badge className="bg-purple-100 text-purple-700 text-[10px]">What-If Active</Badge>}
                  </CardTitle>
                  {whatIfEnabled && (
                    <CardDescription className="text-xs">
                      Showing simulated values with % change from original
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Metric</th>
                          {selectedScenarios.filter(s => s !== null).map((s, idx) => (
                            <th key={s.id} className="text-right py-3 px-4 font-medium" style={{ color: colors[idx] }}>
                              <div className="flex flex-col items-end">
                                <span>{s.name.length > 20 ? s.name.substring(0, 20) + '...' : s.name}</span>
                                <span className="text-[10px] text-slate-400">{formatDate(s.created_at)}</span>
                                {whatIfEnabled && (
                                  <span className="text-[10px] text-purple-500">
                                    LEI: {(whatIfSliders[s.id] ?? (s.configuration?.lei_improvement || 0)).toFixed(0)}%
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                          {selectedCount === 2 && !whatIfEnabled && (
                            <th className="text-right py-3 px-4 font-medium text-slate-600">Difference</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {/* ROI Row */}
                        <tr className={`border-b hover:bg-slate-50 ${whatIfEnabled && selectedMetrics.includes('roi') ? 'bg-purple-50/30' : ''}`}>
                          <td className="py-3 px-4 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-teal-600" />
                            ROI
                            {whatIfEnabled && selectedMetrics.includes('roi') && (
                              <Badge variant="outline" className="text-[9px] h-4 border-purple-300 text-purple-600">Simulated</Badge>
                            )}
                          </td>
                          {selectedScenarios.filter(s => s !== null).map((s, idx) => {
                            const original = getMetricValue(s, 'roi');
                            const effective = getEffectiveMetricValue(s, 'roi');
                            const changePercent = original > 0 ? ((effective - original) / original * 100) : 0;
                            const showChange = whatIfEnabled && selectedMetrics.includes('roi') && Math.abs(changePercent) > 0.1;
                            
                            return (
                              <td key={s.id} className="text-right py-3 px-4">
                                <div className="flex flex-col items-end">
                                  <span className="font-semibold">{(effective / 100).toFixed(2)}x</span>
                                  {showChange && (
                                    <span className={`text-[10px] font-medium ${changePercent > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                      {changePercent > 0 ? '+' : ''}{changePercent.toFixed(1)}% from {(original / 100).toFixed(1)}x
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          {selectedCount === 2 && !whatIfEnabled && (
                            <td className="text-right py-3 px-4">
                              <DifferenceIndicator diff={getDifference(
                                getMetricValue(selectedScenarios[0], 'roi'),
                                getMetricValue(selectedScenarios[1], 'roi')
                              )} />
                            </td>
                          )}
                        </tr>

                        {/* Financial Impact Row */}
                        <tr className={`border-b hover:bg-slate-50 ${whatIfEnabled && selectedMetrics.includes('financial_impact') ? 'bg-purple-50/30' : ''}`}>
                          <td className="py-3 px-4 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-emerald-600" />
                            Financial Impact
                            {whatIfEnabled && selectedMetrics.includes('financial_impact') && (
                              <Badge variant="outline" className="text-[9px] h-4 border-purple-300 text-purple-600">Simulated</Badge>
                            )}
                          </td>
                          {selectedScenarios.filter(s => s !== null).map((s, idx) => {
                            const original = getMetricValue(s, 'financial_impact');
                            const effective = getEffectiveMetricValue(s, 'financial_impact');
                            const changePercent = original > 0 ? ((effective - original) / original * 100) : 0;
                            const showChange = whatIfEnabled && selectedMetrics.includes('financial_impact') && Math.abs(changePercent) > 0.1;
                            
                            return (
                              <td key={s.id} className="text-right py-3 px-4">
                                <div className="flex flex-col items-end">
                                  <span className="font-semibold">{formatCurrency(Math.round(effective, currency))}</span>
                                  {showChange && (
                                    <span className={`text-[10px] font-medium ${changePercent > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                      {changePercent > 0 ? '+' : ''}{changePercent.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          {selectedCount === 2 && !whatIfEnabled && (
                            <td className="text-right py-3 px-4">
                              <DifferenceIndicator diff={getDifference(
                                getMetricValue(selectedScenarios[0], 'financial_impact'),
                                getMetricValue(selectedScenarios[1], 'financial_impact')
                              )} />
                            </td>
                          )}
                        </tr>

                        {/* Net Benefit Row */}
                        <tr className={`border-b hover:bg-slate-50 ${whatIfEnabled && selectedMetrics.includes('net_benefit') ? 'bg-purple-50/30' : ''}`}>
                          <td className="py-3 px-4 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-teal-600" />
                            Net Benefit
                            {whatIfEnabled && selectedMetrics.includes('net_benefit') && (
                              <Badge variant="outline" className="text-[9px] h-4 border-purple-300 text-purple-600">Simulated</Badge>
                            )}
                          </td>
                          {selectedScenarios.filter(s => s !== null).map((s, idx) => {
                            const original = getMetricValue(s, 'net_benefit');
                            const effective = getEffectiveMetricValue(s, 'net_benefit');
                            const changePercent = original > 0 ? ((effective - original) / original * 100) : 0;
                            const showChange = whatIfEnabled && selectedMetrics.includes('net_benefit') && Math.abs(changePercent) > 0.1;
                            
                            return (
                              <td key={s.id} className="text-right py-3 px-4">
                                <div className="flex flex-col items-end">
                                  <span className="font-semibold">{formatCurrency(Math.round(effective, currency))}</span>
                                  {showChange && (
                                    <span className={`text-[10px] font-medium ${changePercent > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                      {changePercent > 0 ? '+' : ''}{changePercent.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          {selectedCount === 2 && !whatIfEnabled && (
                            <td className="text-right py-3 px-4">
                              <DifferenceIndicator diff={getDifference(
                                getMetricValue(selectedScenarios[0], 'net_benefit'),
                                getMetricValue(selectedScenarios[1], 'net_benefit')
                              )} />
                            </td>
                          )}
                        </tr>

                        {/* KPI Uplift Row */}
                        <tr className={`border-b hover:bg-slate-50 ${whatIfEnabled && selectedMetrics.includes('kpi_uplift') ? 'bg-purple-50/30' : ''}`}>
                          <td className="py-3 px-4 flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-purple-600" />
                            KPI Uplift
                            {whatIfEnabled && selectedMetrics.includes('kpi_uplift') && (
                              <Badge variant="outline" className="text-[9px] h-4 border-purple-300 text-purple-600">Simulated</Badge>
                            )}
                          </td>
                          {selectedScenarios.filter(s => s !== null).map((s, idx) => {
                            const original = getMetricValue(s, 'kpi_uplift');
                            const effective = getEffectiveMetricValue(s, 'kpi_uplift');
                            const changePercent = original > 0 ? ((effective - original) / original * 100) : 0;
                            const showChange = whatIfEnabled && selectedMetrics.includes('kpi_uplift') && Math.abs(changePercent) > 0.1;
                            
                            return (
                              <td key={s.id} className="text-right py-3 px-4">
                                <div className="flex flex-col items-end">
                                  <span className="font-semibold">{effective.toFixed(2)}%</span>
                                  {showChange && (
                                    <span className={`text-[10px] font-medium ${changePercent > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                      {changePercent > 0 ? '+' : ''}{changePercent.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          {selectedCount === 2 && !whatIfEnabled && (
                            <td className="text-right py-3 px-4">
                              <DifferenceIndicator diff={getDifference(
                                getMetricValue(selectedScenarios[0], 'kpi_uplift'),
                                getMetricValue(selectedScenarios[1], 'kpi_uplift')
                              )} />
                            </td>
                          )}
                        </tr>

                        {/* Expected Value Recovery Row — only when at least
                            one selected run was modelled from a Mission
                            Control constraint. Recovery = the slice of the
                            leak this run's KPI uplift wins back (capped at
                            the leak itself). Highest recovery is ranked. */}
                        {selectedScenarios.some(s => s?.constraint_context?.label) && (() => {
                          const live = selectedScenarios.filter(s => s !== null);
                          const recoveries = live.map(s => {
                            const ctx = s.constraint_context;
                            if (!ctx?.label || !Number.isFinite(Number(ctx.leak))) return null;
                            return Math.min(getEffectiveMetricValue(s, 'kpi_uplift'), Number(ctx.leak));
                          });
                          const tagged = recoveries.filter(r => r !== null);
                          const best = Math.max(...tagged);
                          return (
                            <tr className="border-b hover:bg-slate-50 bg-orange-50/40" data-testid="expected-value-recovery-row">
                              <td className="py-3 px-4 flex items-center gap-2">
                                <Target className="h-4 w-4 text-orange-600" />
                                Expected Value Recovery
                              </td>
                              {live.map((s, idx) => {
                                const rec = recoveries[idx];
                                const isBest = rec !== null && tagged.length > 1 && rec === best;
                                return (
                                  <td key={s.id} className="text-right py-3 px-4">
                                    {rec === null ? (
                                      <span className="text-slate-300">—</span>
                                    ) : (
                                      <div className="flex flex-col items-end">
                                        <span className={`font-semibold ${isBest ? 'text-emerald-600' : ''}`}>+{rec.toFixed(1)}%</span>
                                        <span className="text-[10px] text-orange-600">
                                          of {s.constraint_context.label} {Number(s.constraint_context.leak)}% leak{isBest ? ' · Best recovery' : ''}
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                              {selectedCount === 2 && !whatIfEnabled && (
                                <td className="text-right py-3 px-4"><span className="text-slate-300">—</span></td>
                              )}
                            </tr>
                          );
                        })()}

                        {/* Payback Period Row */}
                        <tr className={`border-b hover:bg-slate-50 ${whatIfEnabled && selectedMetrics.includes('payback') ? 'bg-purple-50/30' : ''}`}>
                          <td className="py-3 px-4 flex items-center gap-2">
                            <Clock className="h-4 w-4 text-orange-600" />
                            Payback Period
                            {whatIfEnabled && selectedMetrics.includes('payback') && (
                              <Badge variant="outline" className="text-[9px] h-4 border-purple-300 text-purple-600">Simulated</Badge>
                            )}
                          </td>
                          {selectedScenarios.filter(s => s !== null).map((s, idx) => {
                            const original = getMetricValue(s, 'payback');
                            const effective = getEffectiveMetricValue(s, 'payback');
                            const changePercent = original > 0 ? ((effective - original) / original * 100) : 0;
                            const showChange = whatIfEnabled && selectedMetrics.includes('payback') && Math.abs(changePercent) > 0.1;
                            
                            return (
                              <td key={s.id} className="text-right py-3 px-4">
                                <div className="flex flex-col items-end">
                                  <span className="font-semibold">{effective.toFixed(1)} months</span>
                                  {showChange && (
                                    <span className={`text-[10px] font-medium ${changePercent < 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                      {changePercent > 0 ? '+' : ''}{changePercent.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          {selectedCount === 2 && !whatIfEnabled && (
                            <td className="text-right py-3 px-4">
                              <DifferenceIndicator 
                                diff={getDifference(
                                  getMetricValue(selectedScenarios[0], 'payback'),
                                  getMetricValue(selectedScenarios[1], 'payback')
                                )} 
                                inverse={true}
                              />
                            </td>
                          )}
                        </tr>

                        {/* Intervention Cost Row (not simulated) */}
                        <tr className="hover:bg-slate-50">
                          <td className="py-3 px-4 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-slate-600" />
                            Intervention Cost
                          </td>
                          {selectedScenarios.filter(s => s !== null).map((s, idx) => (
                            <td key={s.id} className="text-right py-3 px-4 font-semibold">
                              {formatCurrency(Math.round(getMetricValue(s, 'intervention_cost', currency)))}
                            </td>
                          ))}
                          {selectedCount === 2 && !whatIfEnabled && (
                            <td className="text-right py-3 px-4">
                              <DifferenceIndicator 
                                diff={getDifference(
                                  getMetricValue(selectedScenarios[0], 'intervention_cost'),
                                  getMetricValue(selectedScenarios[1], 'intervention_cost')
                                )} 
                                inverse={true}
                              />
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bar Chart */}
                <Card className={whatIfEnabled ? 'border-purple-200' : ''}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      ROI & Impact Comparison
                      {whatIfEnabled && <Badge className="bg-purple-100 text-purple-700 text-[10px]">What-If Active</Badge>}
                    </CardTitle>
                    <CardDescription>
                      {whatIfEnabled 
                        ? 'Gray bars show original values, colored bars show simulated values'
                        : 'Side-by-side comparison of key metrics'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[450px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={chartData} 
                          margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                          barCategoryGap="25%"
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                          <XAxis 
                            dataKey="name" 
                            tick={(props) => {
                              const { x, y, payload } = props;
                              return (
                                <g transform={`translate(${x},${y + 8})`}>
                                  <text 
                                    textAnchor="end"
                                    fill="#475569"
                                    fontSize={11}
                                    transform="rotate(-40)"
                                  >
                                    {payload.value}
                                  </text>
                                </g>
                              );
                            }}
                            interval={0}
                            tickLine={{ stroke: '#cbd5e1' }}
                            axisLine={{ stroke: '#cbd5e1' }}
                            height={90}
                          />
                          <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                          <Tooltip 
                            formatter={(value, name) => {
                              const isRoi = name.includes('ROI');
                              if (isRoi) return [`${value.toFixed(2)}x`, name];
                              return [`${value.toFixed(2)}%`, name];
                            }}
                            labelFormatter={(label, payload) => {
                              if (payload && payload[0]) {
                                return payload[0].payload.fullName || label;
                              }
                              return label;
                            }}
                          />
                          <Legend wrapperStyle={{ paddingTop: '10px' }} />
                          {whatIfEnabled ? (
                            <>
                              {/* Original values (gray/muted) */}
                              <Bar dataKey="ROI (Original)" fill="#cbd5e1" name="ROI Original" radius={[4, 4, 0, 0]} />
                              <Bar dataKey="KPI Uplift (Original)" fill="#94a3b8" name="KPI Original" radius={[4, 4, 0, 0]} />
                              {/* Simulated values (colored) */}
                              <Bar dataKey="ROI (Simulated)" fill="#0d9488" name="ROI Simulated" radius={[4, 4, 0, 0]} />
                              <Bar dataKey="KPI Uplift (Simulated)" fill="#9333ea" name="KPI Simulated" radius={[4, 4, 0, 0]} />
                            </>
                          ) : (
                            <>
                              <Bar dataKey="ROI" fill="#0d9488" name="ROI (x)" radius={[4, 4, 0, 0]} />
                              <Bar dataKey="KPI Uplift" fill="#1e3a5f" name="KPI Uplift (%)" radius={[4, 4, 0, 0]} />
                            </>
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Radar Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Performance Profile</CardTitle>
                    <CardDescription>Multi-dimensional comparison</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={radarData}>
                          <PolarGrid stroke="#e2e8f0" />
                          <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fontSize: 10 }} />
                          {selectedScenarios.filter(s => s !== null).map((s, idx) => (
                            <Radar
                              key={s.id}
                              name={s.name}
                              dataKey={`scenario${idx + 1}`}
                              stroke={colors[idx]}
                              fill={colors[idx]}
                              fillOpacity={0.2}
                            />
                          ))}
                          <Legend />
                          <Tooltip />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {/* Empty State */}
          {scenarios.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-16">
                <div className="text-center">
                  <GitCompare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900">No Scenarios to Compare</h3>
                  <p className="text-slate-500 mt-2 max-w-md mx-auto">
                    Run some analyses and save them as scenarios first, then come back to compare.
                  </p>
                  <Button
                    onClick={() => navigate('/configure')}
                    className="mt-6 bg-navy-700 hover:bg-navy-800"
                  >
                    Create Analysis
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {filteredScenarios.length > 0 && selectedCount < 2 && (
            <Card className="border-dashed border-teal-200 bg-teal-50/30">
              <CardContent className="py-8">
                <div className="text-center">
                  <p className="text-slate-600">
                    Select at least 2 scenarios above to start comparing
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== PROJECTION VS ACTUAL TAB ==================== */}
        <TabsContent value="accuracy" className="mt-6 space-y-6">
          <ProjectionAccuracyTab
            projectedPairs={projectedPairs}
            setViewMode={setViewMode}
            currency={currency}
          />
        </TabsContent>

        {/* ==================== TRENDS TAB ==================== */}
        <TabsContent value="trends" className="mt-6 space-y-6">
          <TrendAnalysisTab
            scenarioGroups={scenarioGroups}
            selectedGroup={selectedGroup}
            handleSelectGroup={handleSelectGroup}
            currency={currency}
            navigate={navigate}
          />
        </TabsContent>
      </Tabs>

      {/* Save Projected Scenario Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-purple-600" />
              {saveMode === 'single' ? 'Save Projected Scenario' : 'Save Projected Set'}
            </DialogTitle>
            <DialogDescription>
              {saveMode === 'single' 
                ? 'Save this simulated scenario with its projected values for future comparison.'
                : `Save all ${selectedScenarios.filter(s => s !== null).length} scenarios with their simulated values as a comparison set.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="projected-name">Scenario Name</Label>
              <Input
                id="projected-name"
                value={projectedName}
                onChange={(e) => setProjectedName(e.target.value)}
                placeholder="Enter a name for the projected scenario"
                data-testid="projected-name-input"
              />
            </div>
            
            {saveMode === 'single' && scenarioToSave && (
              <div className="bg-purple-50 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-purple-700">Projected Values Preview:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-600">LEI Improvement:</span>
                    <span className="font-medium">{(whatIfSliders[scenarioToSave.id] ?? (scenarioToSave.configuration?.lei_improvement || 0)).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">ROI:</span>
                    <span className="font-medium">{(getEffectiveMetricValue(scenarioToSave, 'roi') / 100).toFixed(2)}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Financial Impact:</span>
                    <span className="font-medium">{formatCurrency(Math.round(getEffectiveMetricValue(scenarioToSave, 'financial_impact', currency)))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">KPI Uplift:</span>
                    <span className="font-medium">{getEffectiveMetricValue(scenarioToSave, 'kpi_uplift').toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            )}
            
            {saveMode === 'batch' && (
              <div className="bg-purple-50 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-purple-700">Scenarios to Save:</p>
                <div className="space-y-1">
                  {selectedScenarios.filter(s => s !== null).map((s, idx) => (
                    <div key={s.id} className="flex items-center justify-between text-xs">
                      <span className="text-slate-600 truncate flex-1">{s.name}</span>
                      <Badge variant="outline" className="text-[9px] ml-2">
                        LEI: {(whatIfSliders[s.id] ?? (s.configuration?.lei_improvement || 0)).toFixed(0)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 p-2 rounded">
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Projected</Badge>
              <span>This badge will identify the scenario as a projection</span>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button 
              onClick={saveProjectedScenario} 
              disabled={isSaving || !projectedName.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-save-projected"
            >
              {isSaving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {saveMode === 'single' ? 'Save Scenario' : `Save ${selectedScenarios.filter(s => s !== null).length} Scenarios`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
};

export default CompareScenarios;
